import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass, Handshake, Layers, MapPin, ShieldCheck, ClipboardList, Home, Mountain, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Chapter, Marquee, MaskedLine, Reveal } from "@/components/motion";
import { AREAS, IMAGES, SERVICES, STEPS } from "@/data/content";

const FEATURES = [
  { icon: Compass, title: "Design-Led, Start to Finish", desc: "Every project begins with your property, vision and lifestyle — and one team accountable for all of it." },
  { icon: Handshake, title: "An Experienced Local Team", desc: "Our own designers, builders and craftspeople bring proven estate and acreage experience across Calgary and Rocky View County." },
  { icon: Layers, title: "One Simple Starting Point", desc: "One conversation begins it all — design, construction and detailing under one roof." },
];

const IDEAL = [
  "Springbank acreage transformations",
  "Bearspaw estate landscaping",
  "Calgary luxury new builds",
  "Multi-phase outdoor renovations",
  "Difficult grading and drainage projects",
  "Complete outdoor-living environments",
];

const TRUST = [
  { icon: MapPin, title: "Local Knowledge", desc: "We live and work here — we understand chinooks, clay soils, frost depth and foothills weather." },
  { icon: ShieldCheck, title: "One Accountable Team", desc: "Design, construction and finishing handled by our own experienced people — one point of contact throughout." },
  { icon: ClipboardList, title: "Clear Project Expectations", desc: "Scope, sequence and investment are discussed openly before any work begins." },
  { icon: Home, title: "Respect for Your Home", desc: "Your property is treated with care — tidy sites, considerate crews and thoughtful scheduling." },
  { icon: Mountain, title: "Complex Acreage Experience", desc: "Grading, drainage, access and scale — the challenges of large properties are our specialty." },
];

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Estate Outdoors Calgary | Luxury Landscaping Calgary, Springbank & Bearspaw"
        description="Luxury landscaping in Calgary, Springbank and Bearspaw. Estate Outdoors Calgary designs and builds luxury backyards, outdoor kitchens and complete estate landscapes with one dedicated local team. Request a private quote."
      />

      {/* ——— HERO ——— */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1C201D]" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img
            src={IMAGES.hero}
            alt="Estate garden with natural stone retaining walls, layered planting and autumn colour in the Calgary foothills"
            className="h-full w-full object-cover"
            fetchpriority="high"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C201D] via-[#1C201D]/45 to-[#1C201D]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C201D]/60 via-transparent to-transparent" />

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 sm:px-8 pb-24 sm:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="eyebrow mb-6 !text-[#C5A059]"
            data-testid="hero-eyebrow"
          >
            Springbank · Bearspaw · Calgary
          </motion.p>
          <h1 className="font-display font-light tracking-tight leading-[1.05] text-[#FAF7F2] text-4xl sm:text-5xl lg:text-7xl max-w-4xl" data-testid="hero-headline">
            <MaskedLine delay={0.3}>Your Dream Backyard</MaskedLine>
            <MaskedLine delay={0.48}><em className="text-[#C5A059] not-italic font-display italic">Starts Here.</em></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#E8ECE8]"
            data-testid="hero-subheading"
          >
            Luxury landscape design and construction, thoughtfully matched to your property, vision and lifestyle.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-3 text-sm text-[#B9BDB4]"
          >
            Serving Springbank, Bearspaw, Calgary and surrounding acreage communities.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/quote" data-testid="hero-quote-btn" className="btn-primary">
              Request a Private Quote <ArrowRight size={16} />
            </Link>
            <Link to="/services" data-testid="hero-services-btn" className="btn-outline-light">
              Explore Our Services
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-3 text-[#FAF7F2]/70"
          aria-hidden="true"
        >
          <span className="text-[0.6rem] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="h-10 w-px bg-[#C5A059]" />
        </motion.div>
      </section>

      <Marquee items={AREAS.map((a) => a.name)} />

      {/* ——— 01 INTRODUCTION ——— */}
      <section className="relative grain py-24 sm:py-32" data-testid="intro-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Chapter number="01" title="The Studio" />
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15]" data-testid="intro-headline">
                Exceptional Outdoor Spaces. <em className="text-[#6B7C6E]">Beautifully Built.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 flex items-end">
              <p className="text-base md:text-lg leading-relaxed text-[#4A524C]">
                Building an extraordinary backyard requires one accountable team. From first sketch to final
                planting, our designers, builders and craftspeople create outdoor environments for estate
                properties and luxury new builds across Calgary and Rocky View County.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={0.1 * i}>
                <div
                  data-testid={`feature-card-${i + 1}`}
                  className="group h-full border border-[#E0D8CC] bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(28,32,29,0.08)] hover:border-[#C5A059]/50"
                >
                  <f.icon size={26} strokeWidth={1.4} className="text-[#C5A059]" aria-hidden="true" />
                  <h3 className="mt-6 font-display text-2xl font-normal leading-snug">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A524C]">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— 02 SERVICES PREVIEW ——— */}
      <section className="bg-[#F2EDE4] py-24 sm:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Chapter number="02" title="Services" />
              <Reveal>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15] max-w-2xl">
                  Every Element of the Estate Landscape
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link to="/services" data-testid="services-view-all-link" className="btn-outline-dark">
                View All Services <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.slice(0, 4).map((s, i) => (
              <Reveal key={s.title} delay={0.08 * i} className={i % 2 === 0 ? "md:col-span-7" : "md:col-span-5"}>
                <Link to="/services" data-testid={`service-card-${i + 1}`} className="group relative block h-[340px] sm:h-[400px] overflow-hidden">
                  <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C201D]/85 via-[#1C201D]/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <span className="font-display italic text-[#C5A059]">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-1 font-display text-2xl sm:text-[1.7rem] font-normal text-[#FAF7F2] leading-snug max-w-sm">{s.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 grid gap-px bg-[#E0D8CC] border border-[#E0D8CC] sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(4).map((s, i) => (
              <Reveal key={s.title} delay={0.05 * i}>
                <Link to="/services" data-testid={`service-row-${i + 5}`} className="group flex h-full items-center justify-between gap-4 bg-[#FAF7F2] px-7 py-6 transition-colors duration-300 hover:bg-white">
                  <div>
                    <span className="font-display italic text-sm text-[#8B5A2B]">{String(i + 5).padStart(2, "0")}</span>
                    <h3 className="font-display text-xl font-normal leading-snug group-hover:text-[#8B5A2B] transition-colors">{s.title}</h3>
                  </div>
                  <ArrowUpRight size={18} className="shrink-0 text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— 03 HOW IT WORKS ——— */}
      <section className="py-24 sm:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Chapter number="03" title="How It Works" />
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15] max-w-2xl">
              A Considered Path From Vision to Ground
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={0.12 * i}>
                <div data-testid={`process-step-${i + 1}`} className="border-t border-[#E0D8CC] pt-8">
                  <span className="font-display text-5xl font-light text-[#C5A059]">{step.num}</span>
                  <h3 className="mt-5 text-lg font-medium tracking-wide">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A524C]">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <Link to="/quote" data-testid="process-quote-btn" className="btn-primary">
              Begin Step One <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ——— 04 IDEAL PROJECTS (dark) ——— */}
      <section className="relative grain bg-[#1C201D] py-24 sm:py-32 text-[#FAF7F2]" data-testid="ideal-projects-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <Chapter number="04" title="Ideal Projects" light />
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15]" data-testid="ideal-headline">
                Created for <em className="text-[#C5A059]">Remarkable Properties.</em>
              </h2>
            </Reveal>
            <ul className="mt-10 space-y-5">
              {IDEAL.map((item, i) => (
                <Reveal key={item} delay={0.07 * i}>
                  <li className="flex items-baseline gap-4 text-base md:text-lg text-[#E8ECE8]" data-testid={`ideal-item-${i + 1}`}>
                    <span className="h-px w-8 shrink-0 translate-y-[-4px] bg-[#C5A059]" aria-hidden="true" />
                    {item}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-3 border border-[#C5A059]/30" aria-hidden="true" />
              <img
                src={IMAGES.firePit}
                alt="Stone fire pit glowing against a Rocky Mountain landscape"
                loading="lazy"
                className="relative h-[420px] sm:h-[520px] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ——— 05 QUOTE CTA ——— */}
      <section className="relative overflow-hidden bg-[#F2EDE4] py-24 sm:py-32" data-testid="quote-cta-section">
        <div className="pointer-events-none absolute -right-24 top-1/2 hidden -translate-y-1/2 select-none font-display text-[22rem] font-light italic leading-none text-[#C5A059]/10 lg:block" aria-hidden="true">
          Eo
        </div>
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Chapter number="05" title="Begin" />
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15]" data-testid="quote-cta-headline">
              Tell Us About Your Dream Backyard.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mx-auto mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524C]">
              Share your property, your vision and your investment range. We'll review every detail personally
              and respond within two business days.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/quote" data-testid="quote-cta-btn" className="btn-primary">
              Request a Private Quote <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ——— 06 TRUST ——— */}
      <section className="py-24 sm:py-32" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Chapter number="06" title="Our Promise" />
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15] max-w-2xl">
              Quiet Confidence, Earned on Every Property
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TRUST.map((t, i) => (
              <Reveal key={t.title} delay={0.07 * i} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div data-testid={`trust-card-${i + 1}`} className="h-full border-l-2 border-[#C5A059]/60 bg-[#F2EDE4] p-7">
                  <t.icon size={22} strokeWidth={1.4} className="text-[#8B5A2B]" aria-hidden="true" />
                  <h3 className="mt-4 text-lg font-medium tracking-wide">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#4A524C]">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— 07 SERVICE AREA ——— */}
      <section className="bg-[#F2EDE4] py-24 sm:py-32" data-testid="service-area-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Chapter number="07" title="Where We Work" />
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15]">
                Proudly Local to Calgary's Estate Communities
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#4A524C]">
                From Springbank acreages and Bearspaw estates to luxury new builds in Aspen Woods and Watermark,
                our design-build team serves Calgary and Rocky View County with deep local
                knowledge of soil, grade and climate.
              </p>
              <Link to="/locations" data-testid="areas-explore-link" className="btn-outline-dark mt-8">
                Explore Service Areas <ArrowUpRight size={15} />
              </Link>
            </Reveal>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#E0D8CC] border border-[#E0D8CC] self-center">
              {AREAS.map((a, i) => (
                <Reveal key={a.name} delay={0.04 * i}>
                  <Link to="/locations" data-testid={`area-chip-${a.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex h-full items-center justify-center bg-[#FAF7F2] px-4 py-6 text-center font-display text-lg transition-colors duration-300 hover:bg-white hover:text-[#8B5A2B]">
                    {a.name}
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ——— THE GUIDE ——— */}
      <section className="py-24 sm:py-28" data-testid="guide-promo-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-12 lg:grid-cols-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto max-w-xs">
              <div className="absolute -inset-3 border border-[#C5A059]/40" aria-hidden="true" />
              <div className="relative -rotate-2 border border-[#E0D8CC] bg-white px-8 py-14 text-center shadow-[0_20px_50px_rgba(28,32,29,0.12)] transition-transform duration-500 hover:rotate-0">
                <p className="eyebrow">2026 Edition</p>
                <p className="mt-5 font-display text-3xl font-light leading-tight">The Acreage Landscape Planning Guide</p>
                <div className="gold-rule my-6" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#758077]">Estate Outdoors · Calgary</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <p className="eyebrow !text-[#8B5A2B]">The Guide</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.15]">
              Plan an Investment Worthy of the Land
            </h2>
            <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524C]">
              Our complimentary planning guide for estate homeowners: how to read your land, phase a multi-year
              build, understand luxury budget ranges, and build in step with the Alberta seasons.
            </p>
            <Link to="/guide" data-testid="guide-promo-btn" className="btn-primary mt-8">
              Download the Guide <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
